import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { newNoticeId, normalizeNoticeRows } from "@/pages/admin/QuoteNoticeEditor";
import {
  AdminEditDrawer,
  AdminEmptyState,
  AdminField,
  AdminPageHeader,
  AdminPagination,
  paginateItems,
  previewText,
} from "@/pages/admin/AdminUx";

export default function QuoteNoticesTab() {
  const [lines, setLines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/quote-notices");
      setLines(normalizeNoticeRows(r.data.lines || [], { withActive: true }));
    } catch {
      toast.error("안내문 목록 로드 실패");
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return lines;
    return lines.filter((row) => String(row.text || "").toLowerCase().includes(needle));
  }, [lines, q]);

  const paged = paginateItems(filtered, page, 10);
  useEffect(() => { setPage(1); }, [q]);
  useEffect(() => { if (page !== paged.page) setPage(paged.page); }, [page, paged.page]);

  const save = async (nextLines = lines) => {
    setSaving(true);
    try {
      await api.put("/admin/quote-notices", {
        lines: normalizeNoticeRows(nextLines, { withActive: true }),
      });
      toast.success("기본 안내문이 저장되었습니다. 이미 생성된 견적에는 적용되지 않습니다.");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "저장 실패");
    }
    setSaving(false);
  };

  const move = (id, dir) => {
    const i = lines.findIndex((row) => row.id === id);
    const j = i + dir;
    if (i < 0 || j < 0 || j >= lines.length) return;
    const next = [...lines];
    [next[i], next[j]] = [next[j], next[i]];
    setLines(normalizeNoticeRows(next, { withActive: true }));
  };

  const add = () => {
    const row = { id: newNoticeId(), text: "※ ", active: true };
    setLines(normalizeNoticeRows([...lines, row], { withActive: true }));
    setEditing({ ...row });
  };

  return (
    <div data-testid="quote-notices">
      <AdminPageHeader
        title="견적서 기본 안내문"
        description="신규 견적 생성 시 활성 문구가 snapshot으로 복사됩니다. 이미 만든 견적의 안내문은 바뀌지 않습니다. 지급조건과는 별개입니다."
        actions={(
          <button type="button" className="bg-white text-black rounded-lg px-3 py-1.5 text-sm" onClick={add}>
            + 안내문 추가
          </button>
        )}
      />

      <div className="admin-toolbar">
        <input
          className="admin-toolbar__search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="안내문 검색"
          data-testid="quote-notice-search"
        />
        <span className="admin-count">{filtered.length}개</span>
      </div>

      {loading ? (
        <div className="text-sm text-white/40">불러오는 중…</div>
      ) : paged.items.length === 0 ? (
        <AdminEmptyState
          message="등록된 견적 안내문이 없습니다."
          action={<button type="button" className="admin-row__btn" onClick={add}>안내문 추가</button>}
        />
      ) : (
        <div className="admin-row-list">
          {paged.items.map((row) => {
            const order = lines.findIndex((r) => r.id === row.id) + 1;
            return (
              <div key={row.id} className="admin-row" data-testid={`quote-notice-row-${row.id}`}>
                <div className="admin-row__main">
                  <div className="admin-row__title">순서 {order}</div>
                  <div className="admin-row__preview">{previewText(row.text) || "(내용 없음)"}</div>
                  <div className="admin-row__meta">{row.active === false ? "비활성" : "활성"}</div>
                </div>
                <div className="admin-row__actions">
                  <button type="button" className="admin-row__btn" onClick={() => setEditing({ ...row })}>수정</button>
                  <button type="button" className="admin-row__btn" onClick={() => move(row.id, -1)}>위</button>
                  <button type="button" className="admin-row__btn" onClick={() => move(row.id, 1)}>아래</button>
                  <button
                    type="button"
                    className="admin-row__btn"
                    onClick={() => setLines(lines.map((r) => (r.id === row.id ? { ...r, active: r.active === false } : r)))}
                  >
                    {row.active === false ? "활성" : "비활성"}
                  </button>
                  <button
                    type="button"
                    className="admin-row__btn admin-row__btn--danger"
                    onClick={() => { if (window.confirm("이 안내문을 삭제할까요?")) setLines(lines.filter((r) => r.id !== row.id)); }}
                  >
                    삭제
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <AdminPagination page={paged.page} pageSize={10} total={filtered.length} onPageChange={setPage} />

      <div className="admin-sticky-actions">
        <button
          type="button"
          disabled={saving || loading}
          onClick={() => save()}
          className="bg-white text-black px-4 py-2 rounded-lg text-sm"
          data-testid="quote-notices-save"
        >
          {saving ? "저장 중…" : "기본 안내문 저장"}
        </button>
      </div>

      <AdminEditDrawer
        open={!!editing}
        title="안내문 수정"
        onClose={() => setEditing(null)}
        footer={(
          <>
            <button type="button" className="admin-row__btn" onClick={() => setEditing(null)}>취소</button>
            <button
              type="button"
              className="bg-white text-black rounded-lg px-4 py-2 text-sm"
              onClick={() => {
                setLines(lines.map((r) => (r.id === editing.id ? { ...r, text: editing.text, active: editing.active } : r)));
                setEditing(null);
              }}
            >
              적용
            </button>
          </>
        )}
      >
        {editing ? (
          <AdminField label="안내문">
            <textarea rows={8} value={editing.text || ""} onChange={(e) => setEditing({ ...editing, text: e.target.value })} />
          </AdminField>
        ) : null}
      </AdminEditDrawer>
    </div>
  );
}
